import style from "@/styles/pages/PropertyDetails/PropertyDetails.module.css";
import { FormProvider, set, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ManualAddress from "@/components/Address/ManualAddress";
import { useDispatch, useSelector } from "react-redux";
import {
    setPAStreetNumber,
    setPAStreetName,
    setPAUnitNumber,
    setPACity,
    setPAProvince,
    setPAPostalCode,
    setPACountry,
    selectPropertyAddress,
    setPAStreetType,
    setPAStreetDirection,
    setPAUnitType,
} from "@/slices/propertyAddressSlice";
import { setProfileComplete } from "@/slices/progressSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { selectApplication, setApplicationProgress, setTotalLoan } from "@/slices/applicationSlice";
import CurrencyInput from "react-currency-input-field";
import { selectPropertyInfo, setHeatingCost, setHeatingType, setLivableArea, setLivableAreaUnit, setMaintenanceFee, setMortgageBalance, setMortgageHolder, setOriginalPurchasePrice, setPropertyTax, setPropertyValue, setPurchasePrice } from "@/slices/propertyInfoSlice";
import { RootState } from "@/store/dtm";
import { selectCoApplicant } from "@/slices/coApplicantSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { addressType } from "@/types/addressType";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { AnimatePresence, motion } from "framer-motion";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto, AssetDto, DealAssetsDto, DealExpensesDto, FinancialAssetDto, LifeInsuranceDto, PropertyExpenseDto, PropertyForCreationDto, RealEstateDto, SubjectPropertyDto, TokenData, VehicleDto } from "@/lib/DTMWebAPIClient.generated";
import { applicationCategory } from "@/types/applicationType";
import dayjs from "dayjs";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@/context/AuthContext";
import { selectPreviousEmployment } from "@/slices/previousEmploymentSlice";
import { setCachedAssets, setCachedCurrentEmployment } from "@/slices/sessionDataSlice";
import { DatePicker } from "@mui/x-date-pickers";
import { addAsset, assetInfo, AssetOwnership, RealEstateAsset, setAssetInfo } from "@/slices/assetSlice";
import { setClickInsightApplicationProgress, setClickInsightFormErrors } from "@/lib/util";
import { addExpense, expenseInfo } from "@/slices/expenseSlice";
import { selectCurrentEmployment } from "@/slices/currentEmploymentSlice";

export default function ReFiPropertyDetails() {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [manualAddressShown, setmanualAddressShown] = useState(false);
    const [showAddress, setShowAddress] = useState(false);
    const [addressSelected, setAddressSelected] = useState(false);
    const [addressSelectionError, setAddressSelectionError] = useState(false);
    const [price, setPrice] = useState("");
    const [propertyValueAmount, setPropertyValueAmount] = useState("");
    const [originalPurchasePriceAmount, setOriginalPurchasePriceAmount] = useState("");
    const [remainingBalanceAmount, setRemainingBalanceAmount] = useState("");
    const [propertyTaxAmount, setPropertyTaxAmount] = useState("");
    const [heatingCostAmount, setHeatingCostAmount] = useState("");
    const [maintenanceAmount, setMaintenanceAmount] = useState("");
    const [originalPurchaseDate, setOriginalPurchaseDate] = useState<string>("");
    const [totalLoanAmount, setTotalLoanAmount] = useState("");
    const [propertySold, setPropertySold] = useState<string>("");
    const [futureUsageText, setFutureUsageText] = useState("");
    const [soldDateText, setSoldDateText] = useState<string>("");
    const [applicantList, setApplicantList] = useState<AssetOwnership[]>([]);
    const [displayError, setDisplayError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const { state } = useLocation();

    const [currentAddressInfo, setCurrentAddressInfo] = useState<addressType>({
        id: 0,
        streetNumber: "",
        streetName: "",
        streetType: "",
        streetDirection: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        unitType: "",
        unitNumber: ""
    });


    const dispatch = useDispatch();

    const propertyAddress = useSelector(selectPropertyAddress);
    const application = useSelector(selectApplication).application;
    const applicant = useSelector((state: RootState) => state.applicant.applicant);
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const coapplicants = useSelector(selectCoApplicant).coapplicants;
    const assets = useSelector((state: RootState) => state.assets.assets);
    const expenses = useSelector((state: RootState) => state.expenses.expenses);
    const currentEmployment = useSelector(selectCurrentEmployment).employments;
    const previousEmployment = useSelector(selectPreviousEmployment);
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            autoAddress: "",
            streetNumber: propertyAddress.address.streetName,
            streetName: propertyAddress.address.streetNumber,
            streetType: propertyAddress.address.streetType,
            streetDirection: propertyAddress.address.streetDirection,
            unitNumber: propertyAddress.address.unitNumber,
            unitType: propertyAddress.address.unitType,
            cityName: propertyAddress.address.city,
            provinceName: propertyAddress.address.province,
            postalCode: propertyAddress.address.postalCode,
            country: propertyAddress.address.country,
            purchasePrice: "",
            propertyValue: "",
            originalPurchasePrice: "",
            originalPurchaseDate: "",
            remainingBalance: "",
            totalLoan: "",
            mortgageHolder: "",
            propertyOwner: "",
            propertyType: "",
            propertyTax: "",
            livableArea: "",
            livableAreaUnit: "",
            heatingCost: "",
            heatingType: "",
            maintenanceFee: "",
            futureUsage: "",
            propertySold: "",
            soldDate: "",

            lot: "",
            block: "",
            plan: "",
            section: ""
        },
    });

    const fields = [
        {
            element: "search",
            field: "",
            mode: window.pca?.fieldMode.SEARCH,
        },

        {
            element: "span-street-address",
            field: "Line1",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-street-address2",
            field: "Line2",
            mode: window.pca?.fieldMode?.POPULATE,
        },
        { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
        {
            element: "span-state",
            field: "ProvinceCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-postcode",
            field: "PostalCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-country",
            field: "CountryName",
            mode: window.pca?.fieldMode.POPULATE,
        },
    ];
    const options = {
        key: "XD95-YY77-BP99-EM55",
    };

    const toggleManualAddress = () => {
        setmanualAddressShown(!manualAddressShown);
        if (!manualAddressShown) {
            formatAddress();
            setShowAddress(false);
        } else {
            setShowAddress(addressSelected);
        }
    };

    const propertySoldChangeHandler = (e: any) => {
        setPropertySold(e.target.value);
        methods.clearErrors("propertySold");
        methods.setValue("propertySold", e.target.value);
    };

    useEffect(() => {
        document.title = "Tell us about your property";

        dispatch(setProfileComplete(true));
        dispatch(setCachedAssets(assets));
        dispatch(setCachedCurrentEmployment(currentEmployment));
        //setClickInsightEvents(application);

        methods.setValue("purchasePrice", propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : propertyInfo.purchasePrice.toString() : "");
        setPrice(propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : propertyInfo.purchasePrice.toString() : "");
        methods.setValue("propertyValue", propertyInfo ? propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString() : "");
        setPropertyValueAmount(propertyInfo ? propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString() : "");
        methods.setValue("originalPurchasePrice", propertyInfo ? propertyInfo.originalPurchasePrice == 0 ? "" : propertyInfo.originalPurchasePrice.toString() : "");
        setOriginalPurchasePriceAmount(propertyInfo ? propertyInfo.originalPurchasePrice == 0 ? "" : propertyInfo.originalPurchasePrice.toString() : "");
        methods.setValue("remainingBalance", propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");
        setRemainingBalanceAmount(propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");
        methods.setValue("totalLoan", propertyInfo ? application.totalLoan == 0 ? "" : application.totalLoan.toString() : "");
        setTotalLoanAmount(propertyInfo ? application.totalLoan == 0 ? "" : application.totalLoan.toString() : "");
        methods.setValue("mortgageHolder", propertyInfo ? propertyInfo.mortgageHolder : "");
        setMortgageHolder(propertyInfo ? propertyInfo.mortgageHolder : "");
        methods.setValue("propertyTax", propertyInfo ? propertyInfo.propertyTax == 0 ? "" : propertyInfo.propertyTax.toString() : "");
        setPropertyTaxAmount(propertyInfo ? propertyInfo.propertyTax == 0 ? "" : propertyInfo.propertyTax.toString() : "");
        methods.setValue("livableArea", propertyInfo ? propertyInfo.livableArea.toString() : "");
        methods.setValue("livableAreaUnit", propertyInfo.livableAreaUnit);
        methods.setValue("heatingCost", propertyInfo ? propertyInfo.heatingCost == 0 ? "" : propertyInfo.heatingCost.toString() : "");
        setHeatingCostAmount(propertyInfo ? propertyInfo.heatingCost == 0 ? "" : propertyInfo.heatingCost.toString() : "");
        methods.setValue("heatingType", propertyInfo ? propertyInfo.heatingType : "");
        setMaintenanceAmount(propertyInfo ? propertyInfo.maintenanceFee == 0 ? "" : propertyInfo.maintenanceFee.toString() : "");

        const control = new window.pca.Address(fields, options);
        control.listen("populate", (address: any) => {
            let streetInfo = address.Line1.split(" ");
            let streetName = "";
            let streetDirection = "";
            let streetType = "";
            let streetComponents: string[] = address.Street.split(" ");
            let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

            //Check if street direction and type exists in the street name
            if (streetDirections.includes(lastStreetComponent)) {
                streetDirection = lastStreetComponent;
                let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
                if (streetTypes.includes(secondLastStreetComponent)) {
                    streetType = secondLastStreetComponent;
                }
                streetComponents.forEach((value, index) => {
                    if (index < streetComponents.length - 2) {
                        streetName += value + " "
                    }
                });
            } else if (streetTypes.includes(lastStreetComponent)) {
                streetType = lastStreetComponent;
                streetComponents.forEach((value, index) => {
                    if (index != streetComponents.length - 1) {
                        streetName += value + " "
                    }
                });
            } else {
                streetComponents.forEach((value, index) => {
                    streetName += value + " "
                });
            }
            streetName = streetName.trim();

            //Check if unit number is embeded in the street number
            let streetNumber = "";
            let streetNumberComponents = streetInfo[0].split("-");
            if (streetNumberComponents.length > 1) {
                streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
            } else {
                streetNumber = streetInfo[0];
            }

            let addressInfo: addressType = {
                id: 0,
                streetNumber: streetNumber,
                streetName: streetName,
                streetType: streetType,
                streetDirection: streetDirection,
                city: address.City,
                province: address.ProvinceCode,
                country: address.CountryName,
                postalCode: address.PostalCode,
                unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
                unitNumber: address.SubBuilding != undefined ? address.SubBuilding : "",
            }
            setCurrentAddressInfo(addressInfo);

            if (address.SubBuilding != undefined && address.SubBuilding != "") {
                methods.setValue("unitNumber", addressInfo.unitNumber);
                methods.setValue("unitType", "unit");
                methods.register("unitType", {
                    required: "Select a unit type",
                    pattern: {
                        value: /^[ #a-zA-Z0-9._-]+$/,
                        message: "Select a unit type",
                    },
                    validate: {
                        unitType: (c) => {
                            if (c.trim() != "") {
                                return true
                            } else {
                                return "Select a unit type";
                            }
                        },
                    },
                });
            }

            methods.setValue("streetNumber", addressInfo.streetNumber);
            methods.setValue("streetName", addressInfo.streetName);
            methods.setValue("streetType", addressInfo.streetType);
            methods.setValue("streetDirection", addressInfo.streetDirection);
            methods.setValue("cityName", addressInfo.city);
            methods.setValue("provinceName", addressInfo.province);
            methods.setValue("postalCode", addressInfo.postalCode);
            methods.setValue("country", addressInfo.country);
            methods.clearErrors();

            if (address.ProvinceName?.trim().toLowerCase() != "ontario") {
                setDisplayError(true);
            } else {
                setDisplayError(false);
            }
        });

        //Refresh Token if it is expired
        if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
            client.apiAuthRefreshtoken(new TokenData({
                accessToken: credential.credential.accessToken,
                refreshToken: credential.credential.refreshToken
            })).then((result) => {
                login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
                updateAppData();
            })
        } else {
            updateAppData();
        }

        if (propertyAddress != undefined && propertyAddress.address != undefined && propertyAddress.address.city != "") {
            try {
                const span = document.getElementById("span-street-address");
                if (span != null) {
                    span.innerText = (propertyAddress.address.unitNumber != "" ? propertyAddress.address.unitNumber + "-" : "") +
                        propertyAddress.address.streetNumber + " " + propertyAddress.address.streetName + " "
                        + propertyAddress.address.streetType + (propertyAddress.address.streetDirection.trim() != "" ? (" " + propertyAddress.address.streetDirection) : "");
                    methods.setValue("autoAddress", span.innerText);
                }
                const span2 = document.getElementById("span-city");
                if (span2 != null) {
                    span2.innerText = propertyAddress.address.city;

                }
                const span3 = document.getElementById("span-state");
                if (span3 != null) {
                    span3.innerText = propertyAddress.address.province;

                }
                const span4 = document.getElementById("span-postcode");
                if (span4 != null) {
                    span4.innerText = propertyAddress.address.postalCode;

                }
                const span5 = document.getElementById("span-country");
                if (span5 != null) {
                    span5.innerText = propertyAddress.address.country;

                }
            } catch (e) { }
            setShowAddress(true);
            setAddressSelected(true);
            setAddressSelectionError(false);

            let addressInfo: addressType = {
                id: propertyAddress.address.id,
                streetNumber: propertyAddress.address.streetNumber,
                streetName: propertyAddress.address.streetName,
                streetType: propertyAddress.address.streetType,
                streetDirection: propertyAddress.address.streetDirection,
                city: propertyAddress.address.city,
                province: propertyAddress.address.province,
                country: propertyAddress.address.country,
                postalCode: propertyAddress.address.postalCode,
                unitType: propertyAddress.address.unitType,
                unitNumber: propertyAddress.address.unitNumber
            }
            setCurrentAddressInfo(addressInfo);
        }

        let applicantList: AssetOwnership[] = [];
        applicantList.push({
            applicantId: applicant.id,
            fullname: `${applicant.firstName} ${applicant.lastName}`,
        });
        setApplicantList(applicantList);

        if (state != null) {
            setEditMode(true);
        }
    }, []);

    useEffect(() => {
        methods.setValue("streetNumber", propertyAddress.address.streetNumber);
        methods.setValue("streetName", propertyAddress.address.streetName);
        methods.setValue("streetType", propertyAddress.address.streetType);
        methods.setValue("streetDirection", propertyAddress.address.streetDirection);
        methods.setValue("unitNumber", propertyAddress.address.unitNumber);
        methods.setValue("unitType", propertyAddress.address.unitType);
        methods.setValue("cityName", propertyAddress.address.city);
        methods.setValue("provinceName", propertyAddress.address.province);
        methods.setValue("postalCode", propertyAddress.address.postalCode);
        methods.setValue("country", propertyAddress.address.country);
        if (assets != undefined && assets.length == 1) {
            let realEstateAsset = assets[0];
            methods.setValue("propertyOwner", realEstateAsset.applicants.length > 0 ? realEstateAsset.applicants[0].toString() : "");
            methods.setValue("propertyType", realEstateAsset.details.realEstate?.propertyType ? realEstateAsset.details.realEstate?.propertyType : "");
            methods.setValue("lot", realEstateAsset.details.realEstate?.lot ? realEstateAsset.details.realEstate?.lot : "");
            methods.setValue("block", realEstateAsset.details.realEstate?.block ? realEstateAsset.details.realEstate?.block : "");
            methods.setValue("plan", realEstateAsset.details.realEstate?.plan ? realEstateAsset.details.realEstate?.plan : "");
            methods.setValue("section", realEstateAsset.details.realEstate?.section ? realEstateAsset.details.realEstate?.section : "");
            methods.setValue("futureUsage", realEstateAsset.details.realEstate?.futureUsage ? realEstateAsset.details.realEstate?.futureUsage : "");
            setOriginalPurchaseDate(realEstateAsset.details.realEstate?.originalPurchaseDate ? realEstateAsset.details.realEstate?.originalPurchaseDate : "");
            methods.setValue("propertySold", realEstateAsset.details.realEstate?.hasPropertyBeenSold ? realEstateAsset.details.realEstate?.hasPropertyBeenSold : "");
            setPropertySold(realEstateAsset.details.realEstate?.hasPropertyBeenSold ? realEstateAsset.details.realEstate?.hasPropertyBeenSold : "");
            setSoldDateText(realEstateAsset.details.realEstate?.soldDate ? realEstateAsset.details.realEstate?.soldDate : "");
            setFutureUsageText(realEstateAsset.details.realEstate?.futureUsage ? realEstateAsset.details.realEstate?.futureUsage : "");
        } else if (assets != undefined && assets.length > 1) {
            assets.forEach(asset => {
                if (asset.assetCategory == "realestate") {
                    let realEstateAsset = asset;
                    methods.setValue("propertyOwner", realEstateAsset.applicants.length > 0 ? realEstateAsset.applicants[0].toString() : "");
                    methods.setValue("propertyType", realEstateAsset.details.realEstate?.propertyType ? realEstateAsset.details.realEstate?.propertyType : "");
                    methods.setValue("lot", realEstateAsset.details.realEstate?.lot ? realEstateAsset.details.realEstate?.lot : "");
                    methods.setValue("block", realEstateAsset.details.realEstate?.block ? realEstateAsset.details.realEstate?.block : "");
                    methods.setValue("plan", realEstateAsset.details.realEstate?.plan ? realEstateAsset.details.realEstate?.plan : "");
                    methods.setValue("section", realEstateAsset.details.realEstate?.section ? realEstateAsset.details.realEstate?.section : "");
                    methods.setValue("futureUsage", realEstateAsset.details.realEstate?.futureUsage ? realEstateAsset.details.realEstate?.futureUsage : "");
                    setOriginalPurchaseDate(realEstateAsset.details.realEstate?.originalPurchaseDate ? realEstateAsset.details.realEstate?.originalPurchaseDate : "");
                    methods.setValue("propertySold", realEstateAsset.details.realEstate?.hasPropertyBeenSold ? realEstateAsset.details.realEstate?.hasPropertyBeenSold : "");
                    setPropertySold(realEstateAsset.details.realEstate?.hasPropertyBeenSold ? realEstateAsset.details.realEstate?.hasPropertyBeenSold : "");
                    setSoldDateText(realEstateAsset.details.realEstate?.soldDate ? realEstateAsset.details.realEstate?.soldDate : "");
                    setFutureUsageText(realEstateAsset.details.realEstate?.futureUsage ? realEstateAsset.details.realEstate?.futureUsage : "");
                }
            });
        }
    }, [applicantList]);

    useEffect(() => {
        if (originalPurchaseDate == "") {
            return;
        }
        let updatedAsset: any = undefined;

        if (assets != undefined && assets.length == 1) {
            updatedAsset = { ...assets[0] };
        } else if (assets != undefined && assets.length > 1) {
            assets.forEach(asset => {
                if (asset.assetCategory == "realestate") {
                    let realEstateAsset = asset;
                    updatedAsset = { ...realEstateAsset };
                }
            });
        }

        if (updatedAsset && updatedAsset.details && updatedAsset.details.realEstate) {
            let assetDetails = { ...updatedAsset.details };
            let realEstateDetails = { ...updatedAsset.details.realEstate };
            if (realEstateDetails) {
                realEstateDetails.originalPurchaseDate = originalPurchaseDate;
            }
            assetDetails.realEstate = realEstateDetails;
            updatedAsset.details = assetDetails;
            dispatch(setAssetInfo(updatedAsset!));
        }
    }, [originalPurchaseDate]);

    useEffect(() => {
        if (soldDateText == "") {
            return;
        }
        if (propertySold == "no") {
            setSoldDateText("");
            methods.setValue("soldDate", "");
        }
        let updatedAsset: any = undefined;

        if (assets != undefined && assets.length == 1) {
            updatedAsset = { ...assets[0] };
        } else if (assets != undefined && assets.length > 1) {
            assets.forEach(asset => {
                if (asset.assetCategory == "realestate") {
                    let realEstateAsset = asset;
                    updatedAsset = { ...realEstateAsset };
                }
            });
        }

        if (updatedAsset && updatedAsset.details && updatedAsset.details.realEstate) {
            let assetDetails = { ...updatedAsset.details };
            let realEstateDetails = { ...updatedAsset.details.realEstate };
            if (realEstateDetails) {
                realEstateDetails.hasPropertyBeenSold = propertySold;
                realEstateDetails.soldDate = propertySold == "yes" ? soldDateText : "";
                console.log(futureUsageText);
                realEstateDetails.futureUsage = futureUsageText;
            }
            assetDetails.realEstate = realEstateDetails;
            updatedAsset.details = assetDetails;
            dispatch(setAssetInfo(updatedAsset!));
        }
    }, [soldDateText, propertySold]);

    useEffect(() => {
        if (futureUsageText == "") {
            return;
        }
        if (futureUsageText != "" && futureUsageText != "selling") {
            setPropertySold("no");
            methods.setValue("propertySold", "no");
        }
        let updatedAsset: any = undefined;

        if (assets != undefined && assets.length == 1) {
            updatedAsset = { ...assets[0] };
        } else if (assets != undefined && assets.length > 1) {
            assets.forEach(asset => {
                if (asset.assetCategory == "realestate") {
                    let realEstateAsset = asset;
                    updatedAsset = { ...realEstateAsset };
                }
            });
        }

        if (updatedAsset && updatedAsset.details && updatedAsset.details.realEstate) {
            let assetDetails = { ...updatedAsset.details };
            let realEstateDetails = { ...updatedAsset.details.realEstate };
            if (realEstateDetails) {
                realEstateDetails.futureUsage = futureUsageText;
            }
            assetDetails.realEstate = realEstateDetails;
            updatedAsset.details = assetDetails;
            dispatch(setAssetInfo(updatedAsset!));
        }
    }, [futureUsageText]);

    useEffect(() => {
        if (!manualAddressShown) {
            methods.unregister([
                "streetNumber",
                "streetName",
                "unitNumber",
                "cityName",
                "provinceName",
                "postalCode",
                "country",
            ]);
            methods.register("autoAddress", {
                required: "Enter your address",
            });
        } else {
            {
                methods.unregister("autoAddress");
                methods.register("streetNumber", {
                    required: "Enter your street number",
                    pattern: {
                        value: /^[ #a-zA-Z0-9._-]+$/,
                        message: " Please enter a valid street number",
                    },
                });
                methods.register("unitNumber", {
                    required: methods.getValues("unitType")?.trim() != "" ? "Enter your unit number" : false,
                    pattern: {
                        value: /^[ #a-zA-Z0-9._-]+$/,
                        message:
                            " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
                    },
                });
                methods.register("unitType", {
                    required: methods.getValues("unitNumber")?.trim() != "" ? "Select a unit type" : false,
                    pattern: {
                        value: /^[ #a-zA-Z0-9._-]+$/,
                        message: "Select a unit type",
                    },
                    validate: {
                        unitType: (c) => {
                            console.log(c);
                            if (c.trim() != "" || methods.getValues("unitNumber")?.trim() == "") {
                                return true
                            } else {
                                return "Select a unit type";
                            }
                        },
                    },
                });
                methods.register("streetName", {
                    required: "Enter your street name",
                    pattern: {
                        value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                        message: " Please enter a valid street name",
                    },
                });
                methods.register("cityName", {
                    required: "Enter your city name",
                    pattern: {
                        value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
                        message: " Please enter a valid city name",
                    },
                });
                methods.register("provinceName", {
                    required: "Select your province name",
                });
                methods.register("postalCode", {
                    required: "Enter your postal code",
                });
                methods.register("country", {
                    required: "Select your country name",
                    validate: {
                        country: (v) => v == "Canada",
                    },
                });
            }
        }
    }, [manualAddressShown, methods]);

    useEffect(() => {
        if (currentAddressInfo.streetNumber && currentAddressInfo.streetNumber.trim() != "" && !manualAddressShown) {
            setShowAddress(true);
            setAddressSelected(true);
            setAddressSelectionError(false);
        }

    }, [currentAddressInfo]);

    const formatAddress = () => {
        const span = document.getElementById("span-street-address");
        const span2 = document.getElementById("span-city");
        const span3 = document.getElementById("span-state");
        const span4 = document.getElementById("span-postcode");
        const span5 = document.getElementById("span-country");

        const streetAddress = span!.innerHTML.split(" ");
        const streetNumber = streetAddress.shift();

        methods.setValue("autoAddress", span!.innerHTML);
        methods.setValue("cityName", span2!.innerHTML);
        methods.setValue("provinceName", span3!.innerHTML);
        methods.setValue("postalCode", span4!.innerHTML);
        methods.setValue("country", span5!.innerHTML);
        methods.setValue("streetName", currentAddressInfo.streetName);

        if (streetNumber?.includes("-")) {
            const unit = streetNumber.split("-")[0];
            const number = streetNumber.split("-")[1];
            methods.setValue("unitNumber", unit!);
            methods.setValue("streetNumber", number!);
        } else {
            methods.setValue("unitNumber", "");
            methods.setValue("streetNumber", streetNumber!);
        }
    };

    const setPropertyAddress = () => {
        dispatch(setPAStreetNumber(methods.getValues("streetNumber")));
        dispatch(setPAStreetName(methods.getValues("streetName")));
        dispatch(setPAStreetType(methods.getValues("streetType")));
        dispatch(setPAStreetDirection(methods.getValues("streetDirection")));
        dispatch(setPAUnitNumber(methods.getValues("unitNumber")));
        dispatch(setPAUnitType(methods.getValues("unitType") != undefined ? methods.getValues("unitType").trim() : ""));
        dispatch(setPACity(methods.getValues("cityName")));
        dispatch(setPAProvince(methods.getValues("provinceName")));
        dispatch(setPAPostalCode(methods.getValues("postalCode")));
        dispatch(setPACountry(methods.getValues("country")));
    };

    const onError = (errors: any) => {
        setClickInsightFormErrors(errors);
        console.log(errors);
        if ((errors.unitType != undefined || errors.unitNumber != undefined) && !manualAddressShown) {
            toggleManualAddress();
        }
        window.scrollTo(0, 0);
    }

    const updateAppData = () => {
        client.apiApplicationsFetch().then((result) => {
            result.coapplicants = coapplicants;
            result.application = application;
            result.previousEmployment = previousEmployment;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }

    const onSubmit = async (data: any) => {
        if ((manualAddressShown && data.provinceName?.trim() != "ON") || displayError) {
            setDisplayError(true);
            return;
        }
        if (data.streetType.trim() == "") {
            window.scrollTo(0, 0);
            methods.setError("streetType", {
                type: "required",
                message: "Select your street type",
            });
            setmanualAddressShown(true);
            setShowAddress(false);
            return;
        }
        if (propertySold == "yes" && soldDateText == "") {
            methods.setError("soldDate", {
                type: "required",
                message: "Enter the sold date.",
            });
            return;
        }
        if (data.propertyType == "condo" && (data.maintenanceFee == undefined || data.maintenanceFee.trim() == "")) {
            methods.setError("maintenanceFee", {
                type: "required",
                message: "Enter the maintenance fee.",
            });
            return;
        }

        if (!addressSelected && !manualAddressShown) {
            setAddressSelectionError(true);
            return;
        }

        if (!manualAddressShown) {
            formatAddress();
        }

        setPropertyAddress();
        dispatch(setPurchasePrice(+price));
        dispatch(setPropertyValue(+propertyValueAmount));
        dispatch(setOriginalPurchasePrice(+originalPurchasePriceAmount));
        dispatch(setMortgageBalance(+remainingBalanceAmount));
        dispatch(setMortgageHolder(data.mortgageHolder));
        dispatch(setTotalLoan(+totalLoanAmount));
        dispatch(setPropertyTax(+propertyTaxAmount));
        dispatch(setLivableArea(+data.livableArea));
        dispatch(setLivableAreaUnit(data.livableAreaUnit));
        dispatch(setHeatingCost(+heatingCostAmount));
        dispatch(setHeatingType(data.heatingType));
        dispatch(setMaintenanceFee(+maintenanceAmount));
        dispatch(setApplicationProgress(14));

        let selectedClosingDate = undefined;
        if (propertyInfo.closingDate != undefined && propertyInfo.closingDate != "") {
            let dateComponents = propertyInfo.closingDate.split("/");
            selectedClosingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }

        let selectedFundingDate = undefined;
        if (propertyInfo.fundingDate != undefined && propertyInfo.fundingDate != "") {
            let dateComponents = propertyInfo.fundingDate.split("/");
            selectedFundingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }

        //Update Property Info on Trellis Backend
        setIsSaving(true);
        await client.apiDealsApplicationsMortgagesSubjectProperty(new PropertyForCreationDto({
            applicationId: 0,
            remainingBalanceAmount: +remainingBalanceAmount,
            downPaymentAmount: application.downPayment,
            totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? (+price - application.downPayment) : +totalLoanAmount,
            newMoneyRequestedAmount: 0,
            subjectProperty: new SubjectPropertyDto({
                originalPurchasePrice: +originalPurchasePriceAmount,
                purchasePrice: application.applicationCategory == applicationCategory.Buy ? +price : +propertyValueAmount,
                propertySalePrice: 0,
                address: new AddressDto({
                    id: 0,
                    streetNumber: methods.getValues("streetNumber"),
                    streetName: methods.getValues("streetName"),
                    streetType: methods.getValues("streetType"),
                    streetDirection: methods.getValues("streetDirection"),
                    unitType: methods.getValues("unitType"),
                    unitNumber: methods.getValues("unitNumber"),
                    city: methods.getValues("cityName"),
                    region: methods.getValues("provinceName"),
                    postalCode: methods.getValues("postalCode"),
                    country: methods.getValues("country"),
                    timeAtResidenceInMonths: 0,
                    residenceStatusType_Name: "",
                    deliveryMode: undefined,
                    modeNumber: undefined,
                    deliveryMode2: undefined,
                    modeNumber2: undefined,
                    isMailing: false
                }),
                propertyTax: +propertyTaxAmount,
                propertyTaxFrequency: "annually",
                mortgageHolder: data.mortgageHolder,
                livableArea: +data.livableArea,
                livableAreaType: data.livableAreaUnit,
                heatingCost: +heatingCostAmount,
                heatingType: data.heatingType,
                heatingCostFrequency: "monthly",
                condoFees: +maintenanceAmount,
                condoFeesFrequency: "monthly",
                futureStatusOfProperty: "",
                occupancyType: propertyInfo.purposeOfProperty != "" ? propertyInfo.purposeOfProperty : "Primary",
                subjectPropertyRentalUnits: propertyInfo.subjectPropertyRentalUnits,
                totalRentalUnits: 0,
                totalRentalProperties: 0,
                ownershipPercentage: propertyInfo.ownershipPercentage,
                hasCurrentRentalProperties: false,
                isPlanningOnRenting: propertyInfo.rentOut || propertyInfo.purposeOfProperty == "I'll rent it out",
                isSeparateLegalUnit: false,
                rent: propertyInfo.monthlyRent,
                estimatedFundingDate: selectedFundingDate,
                closingDate: selectedClosingDate,
                conditionOfFinancingDate: undefined,
                rentalIncome: propertyInfo.monthlyRent
            })
        })).then((result) => {

            if (applicant.isProspect) {
                saveReFiAsset(data);
            } else {
                saveRefiAssetMember(data);
            }

        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving property information", error);
        });
    };

    const saveReFiAsset = async (data: any) => {
        let assetId = "0";
        if (assets == undefined || assets.length == 0) {
            let newAssetInfo: assetInfo = {
                displayId: "1",
                id: 0,
                visible: true,
                assetCategory: "realestate",
                assetType: "",
                origin: "",
                value: propertyInfo.propertyValue,
                valueText: "",
                description: "",
                downPaymentValue: 0,
                downPaymentValueText: "",
                applicants: [+data.propertyOwner],
                details: {
                    nonRegistered: undefined,
                    registered: undefined,
                    realEstate: {
                        occupancyType: "primary",
                        propertyType: data.propertyType,
                        rentalComponent: "no",
                        numOfProperties: "0",
                        address: {
                            displayId: "0",
                            streetNumber: data.streetNumber,
                            streetName: data.streetName,
                            streetType: data.streetType,
                            streetDirection: data.streetDirection,
                            unitType: data.unitType,
                            unitNumber: data.unitNumber,
                            city: data.city,
                            province: data.province,
                            postalCode: data.postalCode,
                            country: data.country,
                        },
                        originalPurchasePrice: data.originalPurchasePrice,
                        originalPurchasePriceText: data.originalPurchasePriceText,
                        originalPurchaseDate: originalPurchaseDate == "Invalid Date" ? "" : originalPurchaseDate,
                        ownershipPercentage: propertyInfo.ownershipPercentage ? propertyInfo.ownershipPercentage.toString() : "",
                        propertyTax: data.propertyTax,
                        propertyTaxText: data.propertyTaxText,
                        heatingCost: data.heatingCost,
                        heatingCostText: data.heatingCostText,
                        maintenanceFee: data.maintenanceFee,
                        maintenanceFeeText: data.maintenanceFeeText,
                        lot: data.lot,
                        block: data.block,
                        plan: data.plan,
                        section: data.section,
                        futureUsage: data.futureUsage,
                        hasPropertyBeenSold: data.propertySold,
                        soldDate: data.soldDate,
                        rentalIncome: data.rentalIncome,
                        rentalIncomeText: data.rentalIncomeText,
                        manualAddressEntered: data.manualAddressEntered,
                        isCurrentResidence: false
                    },
                    vehicle: undefined,
                    lifeInsurance: undefined
                }
            };
            dispatch(addAsset(newAssetInfo));
        } else {

            let asset = { ...assets[0] };
            asset.applicants = [+data.propertyOwner];
            assetId = asset.id.toString();
            let newAssetDetails = { ...asset.details };
            newAssetDetails.realEstate = {
                occupancyType: "primary",
                propertyType: data.propertyType,
                rentalComponent: "no",
                numOfProperties: "0",
                address: {
                    displayId: "0",
                    streetNumber: data.streetNumber,
                    streetName: data.streetName,
                    streetType: data.streetType,
                    streetDirection: data.streetDirection,
                    unitType: data.unitType,
                    unitNumber: data.unitNumber,
                    city: data.city,
                    province: data.province,
                    postalCode: data.postalCode,
                    country: data.country,
                },
                originalPurchasePrice: data.originalPurchasePrice,
                originalPurchasePriceText: data.originalPurchasePriceText,
                originalPurchaseDate: originalPurchaseDate == "Invalid Date" ? "" : originalPurchaseDate,
                ownershipPercentage: propertyInfo.ownershipPercentage ? propertyInfo.ownershipPercentage.toString() : "",
                propertyTax: data.propertyTax,
                propertyTaxText: data.propertyTaxText,
                heatingCost: data.heatingCost,
                heatingCostText: data.heatingCostText,
                maintenanceFee: data.maintenanceFee,
                maintenanceFeeText: data.maintenanceFeeText,
                lot: data.lot,
                block: data.block,
                plan: data.plan,
                section: data.section,
                futureUsage: data.futureUsage,
                hasPropertyBeenSold: data.propertySold,
                soldDate: data.soldDate,
                rentalIncome: data.rentalIncome,
                rentalIncomeText: data.rentalIncomeText,
                manualAddressEntered: data.manualAddressEntered,
                isCurrentResidence: false
            }
            asset.details = newAssetDetails;

            dispatch(setAssetInfo(asset));
        }

        let originalDate = undefined;
        if (originalPurchaseDate.trim() != "" && originalPurchaseDate != "Invalid Date") {
            let dateComponents = originalPurchaseDate.split("/");
            originalDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        let soldDate = undefined;
        if (soldDateText.trim() != "" && soldDateText != "Invalid Date") {
            let dateComponents = soldDateText.split("/");
            soldDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        let realEstate: RealEstateDto[] = [];
        realEstate.push(new RealEstateDto({
            occupancyType_Name: "primary",
            propertyType_Name: data.propertyType,
            rentalComponent: false,
            numberOfProperties: 0,
            assetAddress_StreetNumber: data.streetNumber,
            assetAddress_StreetName: data.streetName,
            assetAddress_StreetType: data.streetType,
            assetAddress_StreetDirection: data.streetDirection,
            assetAddress_UnitType: data.unitType,
            assetAddress_UnitNumber: data.unitNumber,
            assetAddress_City: data.cityName,
            assetAddress_PostalCode: data.postalCode,
            assetAddress_Region: data.provinceName,
            assetAddress_Country: data.country,
            origPurchasePrice: originalPurchasePriceAmount == "" ? 0 : +originalPurchasePriceAmount,
            origPurchaseDate: originalDate,
            lot: data.lot,
            block: data.block,
            plan: data.plan,
            section: data.section,
            futureUsageType_Name: data.futureUsage,
            hasPropertyBeenSold: data.propertySold == "yes",
            soldDate: soldDate,
            rentalIncome: 0,
            id: +assetId,
            assetCategoryType_Name: "realestate",
            assetType_Name: "",
            origin_Name: "manual",
            value: propertyInfo.propertyValue,
            description: "",
            downPaymentValue: 0,
            applicants: [applicant.id],
        }));

        updateAssets(realEstate);
    }

    const saveRefiAssetMember = async (data: any) => {
        let realEstateAsset: any;
        let assetId = "0";
        let id = 1;
        assets.forEach((a: assetInfo) => {
            if (a.assetCategory == "realestate" && a.details.realEstate != undefined) {
                realEstateAsset = { ...a };
            }
            id++;
        });
        if (realEstateAsset == undefined) {
            let newAssetInfo: assetInfo = {
                displayId: id.toString(),
                id: 0,
                visible: true,
                assetCategory: "realestate",
                assetType: "",
                origin: "",
                value: propertyInfo.propertyValue,
                valueText: "",
                description: "",
                downPaymentValue: 0,
                downPaymentValueText: "",
                applicants: [+data.propertyOwner],
                details: {
                    nonRegistered: undefined,
                    registered: undefined,
                    realEstate: {
                        occupancyType: "primary",
                        propertyType: data.propertyType,
                        rentalComponent: "no",
                        numOfProperties: "0",
                        address: {
                            displayId: "0",
                            streetNumber: data.streetNumber,
                            streetName: data.streetName,
                            streetType: data.streetType,
                            streetDirection: data.streetDirection,
                            unitType: data.unitType,
                            unitNumber: data.unitNumber,
                            city: data.city,
                            province: data.province,
                            postalCode: data.postalCode,
                            country: data.country,
                        },
                        originalPurchasePrice: data.originalPurchasePrice,
                        originalPurchasePriceText: data.originalPurchasePriceText,
                        originalPurchaseDate: originalPurchaseDate == "Invalid Date" ? "" : originalPurchaseDate,
                        ownershipPercentage: propertyInfo.ownershipPercentage ? propertyInfo.ownershipPercentage.toString() : "",
                        propertyTax: data.propertyTax,
                        propertyTaxText: data.propertyTaxText,
                        heatingCost: data.heatingCost,
                        heatingCostText: data.heatingCostText,
                        maintenanceFee: data.maintenanceFee,
                        maintenanceFeeText: data.maintenanceFeeText,
                        lot: data.lot,
                        block: data.block,
                        plan: data.plan,
                        section: data.section,
                        futureUsage: data.futureUsage,
                        hasPropertyBeenSold: data.propertySold,
                        soldDate: data.soldDate,
                        rentalIncome: data.rentalIncome,
                        rentalIncomeText: data.rentalIncomeText,
                        manualAddressEntered: data.manualAddressEntered,
                        isCurrentResidence: false
                    },
                    vehicle: undefined,
                    lifeInsurance: undefined
                }
            };
            dispatch(addAsset(newAssetInfo));
        } else {
            realEstateAsset.applicants = [+data.propertyOwner];
            assetId = realEstateAsset.id.toString();
            let newAssetDetails = { ...realEstateAsset.details };
            newAssetDetails.realEstate = {
                occupancyType: "primary",
                propertyType: data.propertyType,
                rentalComponent: "no",
                numOfProperties: "0",
                address: {
                    displayId: "0",
                    streetNumber: data.streetNumber,
                    streetName: data.streetName,
                    streetType: data.streetType,
                    streetDirection: data.streetDirection,
                    unitType: data.unitType,
                    unitNumber: data.unitNumber,
                    city: data.city,
                    province: data.province,
                    postalCode: data.postalCode,
                    country: data.country,
                },
                originalPurchasePrice: data.originalPurchasePrice,
                originalPurchasePriceText: data.originalPurchasePriceText,
                originalPurchaseDate: originalPurchaseDate == "Invalid Date" ? "" : originalPurchaseDate,
                lot: data.lot,
                block: data.block,
                plan: data.plan,
                section: data.section,
                futureUsage: data.futureUsage,
                hasPropertyBeenSold: data.propertySold,
                soldDate: data.soldDate,
                rentalIncome: data.rentalIncome,
                rentalIncomeText: data.rentalIncomeText,
                manualAddressEntered: data.manualAddressEntered,
            }
            realEstateAsset.details = newAssetDetails;
            dispatch(setAssetInfo(realEstateAsset));
        }

        let originalDate = undefined;
        if (originalPurchaseDate.trim() != "" && originalPurchaseDate != "Invalid Date") {
            let dateComponents = originalPurchaseDate.split("/");
            originalDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        let soldDate = undefined;
        if (soldDateText.trim() != "" && soldDateText != "Invalid Date") {
            let dateComponents = soldDateText.split("/");
            soldDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        let realEstate: RealEstateDto[] = [];
        realEstate.push(new RealEstateDto({
            occupancyType_Name: "primary",
            propertyType_Name: data.propertyType,
            rentalComponent: false,
            numberOfProperties: 0,
            assetAddress_StreetNumber: data.streetNumber,
            assetAddress_StreetName: data.streetName,
            assetAddress_StreetType: data.streetType,
            assetAddress_StreetDirection: data.streetDirection,
            assetAddress_UnitType: data.unitType,
            assetAddress_UnitNumber: data.unitNumber,
            assetAddress_City: data.cityName,
            assetAddress_PostalCode: data.postalCode,
            assetAddress_Region: data.provinceName,
            assetAddress_Country: data.country,
            origPurchasePrice: originalPurchasePriceAmount == "" ? 0 : +originalPurchasePriceAmount,
            origPurchaseDate: originalDate,
            lot: data.lot,
            block: data.block,
            plan: data.plan,
            section: data.section,
            futureUsageType_Name: data.futureUsage,
            hasPropertyBeenSold: data.propertySold == "yes",
            soldDate: soldDate,
            rentalIncome: 0,
            id: +assetId,
            assetCategoryType_Name: "realestate",
            assetType_Name: "",
            origin_Name: "manual",
            value: propertyInfo.propertyValue,
            description: "",
            downPaymentValue: 0,
            applicants: [+data.propertyOwner]
        }));

        updateAssets(realEstate);
    }

    const updateAssets = async (realEstate: RealEstateDto[]) => {
        await client.apiAssetsPost(new DealAssetsDto({
            nonRegistered: [],
            registered: [],
            realEstate: realEstate,
            vehicle: [],
            lifeInsurance: [],
            other: [],
            downPayment: [],
        })).then((result: DealAssetsDto) => {
            // Handle the result if needed
            setIsSaving(false);
            if (result.nonRegistered && result.nonRegistered.length > 0) {
                result.nonRegistered.forEach((asset: FinancialAssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.nonRegistered && a.details.nonRegistered.financialInstitution === asset.fiName &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.registered && result.registered.length > 0) {
                result.registered.forEach((asset: FinancialAssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.registered && a.details.registered.financialInstitution === asset.fiName &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.realEstate && result.realEstate.length > 0) {
                result.realEstate.forEach((asset: RealEstateDto) => {

                    let existingExpense = expenses.find(exp => exp.assetId === asset.id);

                    //Append Property Tax as Expense for Real Estate Asset
                    let propertyExpenseDto = new PropertyExpenseDto({
                        assetId: asset.id ? asset.id : 0,
                        expenseCategoryType: "property",
                        taxAmount: +propertyTaxAmount,
                        taxFrequency: "annually",
                        condoFeesAmount: +maintenanceAmount,
                        condoFeesFrequency: "monthly",
                        utilityFeesAmount: +heatingCostAmount,
                        utilityFeesFrequency: "monthly",
                        id: existingExpense ? existingExpense.id : 0,
                        applicants: realEstate[0].applicants
                    });
                    client.apiExpensesPost(new DealExpensesDto({
                        property: [propertyExpenseDto],
                    })).then((expenseResult) => {
                        if (expenseResult.property && expenseResult.property.length > 0) {
                            let expense = expenseResult.property[0];

                            if (existingExpense == undefined){
                                let newExpense: expenseInfo = {
                                    displayId: (expenses.length + 1).toString(),
                                    id: expense.id ? expense.id : 0,
                                    assetId: expense.assetId ? expense.assetId : 0,
                                    expenseCategory: "property",
                                    propertyTax: expense.taxAmount ? expense.taxAmount : 0,
                                    heatingCost: expense.utilityFeesAmount ? expense.utilityFeesAmount : 0,
                                    maintenanceFee: expense.condoFeesAmount ? expense.condoFeesAmount : 0,
                                    ownershipPercentage: expense.ownershipPercentage ? expense.ownershipPercentage : 0,
                                    applicants: expense.applicants ? expense.applicants : [],
                                };
                                dispatch(addExpense(newExpense));
                            }
                        }

                    }).catch((error) => {
                        console.log("Error saving property expense for asset");
                    });

                    assets.forEach((a: assetInfo) => {
                        if (a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue && asset.id == 0) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.vehicle && result.vehicle.length > 0) {
                result.vehicle.forEach((asset: VehicleDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.vehicle && a.details.vehicle.ownership === asset.vehicleOwnershipType_Name && a.details.vehicle.make === asset.make &&
                            a.details.vehicle.model === asset.model && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.lifeInsurance && result.lifeInsurance.length > 0) {
                result.lifeInsurance.forEach((asset: LifeInsuranceDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.lifeInsurance && a.details.lifeInsurance.cashSurrenderValue === asset.cashSurrenderValue &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.downPayment && result.downPayment.length > 0) {
                result.downPayment.forEach((asset: AssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.assetType === asset.assetType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.other && result.other.length > 0) {
                result.other.forEach((asset: AssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.assetCategory === asset.assetCategoryType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }


            if (editMode) {
                navigate("/single/property-purpose",  { state: { editMode: true } });
            } else {
                dispatch(setApplicationProgress(14));
                setClickInsightApplicationProgress('Your home - Tell us about your property');
                navigate("/single/property-purpose");
            }

        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving asset details", error);
            if (editMode) {
                navigate("/single/property-purpose",  { state: { editMode: true } });
            } else {
                dispatch(setApplicationProgress(14));
                navigate("/single/property-purpose");
            }
        });
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">Tell us about your property</h1>
                        <p className="sub-heading">Share the address, and some other details, about your new property.</p>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    Cannot proceed, property must be located in Ontario.
                                </span>
                            </div>
                        }
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="col-span-1">
                                        <div>
                                            <label htmlFor="search">Property Address</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/magnifying-glass-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.autoAddress
                                                                ? "hidden"
                                                                : "",
                                                            "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="search"
                                                        height={5}
                                                        width={5}
                                                    />
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.autoAddress
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    autoComplete="nope"
                                                    className={clsx(
                                                        methods.formState?.errors?.autoAddress?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2"
                                                    )}
                                                    id="search"
                                                    type="text"
                                                    placeholder="Start typing address"
                                                    {...methods.register("autoAddress")}
                                                />
                                            </div>
                                            {addressSelectionError && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    You must select an address from the list of options
                                                </span>
                                            )}
                                            <div
                                                className={clsx("ml-2 mt-2", showAddress ? "" : "hidden")}
                                            >
                                                <p id="formatted-address"></p>
                                                <span id="span-street-address"></span>
                                                <br />
                                                <span id="span-city"></span>&nbsp;
                                                <span id="span-state"></span>&nbsp;
                                                <span id="span-postcode"></span>
                                                <br />
                                                <span id="span-country"></span>
                                            </div>
                                            <div
                                                className="flex relative mt-3"
                                            >
                                                <p className="active underline cursor-pointer" onClick={() => toggleManualAddress()}>{manualAddressShown ? "Hide address fields" : "Enter address manually"}</p>
                                            </div>
                                        </div>
                                        <AnimatePresence>
                                            {manualAddressShown &&
                                                <motion.div
                                                    key={0}
                                                    initial={{ y: -100, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                ><ManualAddress addressData={currentAddressInfo} manualAddressToggled={manualAddressShown} forceOntario={true} /></motion.div>
                                            }
                                        </AnimatePresence>
                                    </div>
                                    <div className="flex md:flex-row flex-col mt-5">
                                        <div className={style.threeColFirst}>
                                            <label htmlFor="realEstateLot">Lot (optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.lot
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        methods.formState?.errors?.lot
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id={"realEstateLot"}
                                                    {...methods.register("lot", {
                                                    })}
                                                    style={{ maxWidth: 160 }}
                                                    maxLength={25}
                                                />
                                            </div>
                                        </div>
                                        <div className={style.threeColFirst}>
                                            <label htmlFor="realEstateBlock">Block (optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.block
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        methods.formState?.errors?.block
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id={"realEstateBlock"}
                                                    {...methods.register("block", {})}
                                                    style={{ maxWidth: 160 }}
                                                    maxLength={25}
                                                />
                                            </div>
                                        </div>
                                        <div className={style.threeColFirst}>
                                            <label htmlFor="realEstatePlan">Plan (optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.plan
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        methods.formState?.errors?.plan
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id={"realEstatePlan"}
                                                    {...methods.register("plan", {})}
                                                    style={{ maxWidth: 160 }}
                                                    maxLength={13}
                                                />
                                            </div>
                                        </div>
                                        <div className={style.threeColFirst}>
                                            <label htmlFor="realEstateSection">Section (optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.section
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        methods.formState?.errors?.section
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id={"realEstateSection"}
                                                    {...methods.register("section", {})}
                                                    style={{ maxWidth: 160 }}
                                                    maxLength={16}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Estimated property value</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.propertyValue
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="propertyValue"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={propertyValueAmount}
                                                    maxLength={10}
                                                    className={clsx(
                                                        methods.formState?.errors?.propertyValue?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("propertyValue", {
                                                        required: "Enter the estimated property value of your home.",
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setPropertyValueAmount(value) : setPropertyValueAmount("")}
                                                />
                                            </div>
                                            {methods.formState?.errors?.propertyValue?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.propertyValue.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Current balance</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.remainingBalance
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="remainingBalance"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={remainingBalanceAmount}
                                                    maxLength={10}
                                                    className={clsx(
                                                        methods.formState?.errors?.remainingBalance?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("remainingBalance", {
                                                        required: "Enter the remaining balance of your existing mortgage",
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setRemainingBalanceAmount(value) : setRemainingBalanceAmount("")}
                                                />
                                            </div>
                                            {methods.formState?.errors?.remainingBalance?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.remainingBalance.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Original purchase price</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.originalPurchasePrice
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="originalPurchasePrice"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={originalPurchasePriceAmount}
                                                    maxLength={10}
                                                    className={clsx(
                                                        methods.formState?.errors?.originalPurchasePrice?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("originalPurchasePrice", {
                                                        required: "Enter the original purchase price your home",
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setOriginalPurchasePriceAmount(value) : setOriginalPurchasePriceAmount("")}
                                                />
                                            </div>
                                            {methods.formState?.errors?.originalPurchasePrice?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.originalPurchasePrice.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColSecond, "mt-5")}>
                                            <label htmlFor="originalPurchaseDate">Original Purchase Date (optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.originalPurchaseDate?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <div>
                                                    <DatePicker
                                                        className={style.datePickerInput}
                                                        format="DD MMM YYYY"
                                                        maxDate={dayjs()}
                                                        value={dayjs(originalPurchaseDate, 'DD/MM/YYYY')}
                                                        onChange={(e) => {
                                                            if (e != undefined) {
                                                                setOriginalPurchaseDate(e.format('DD/MM/YYYY'));
                                                            }
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                InputProps: {
                                                                    sx: {
                                                                        borderRadius: '12px',
                                                                        backgroundColor: 'white',
                                                                        padding: "0px",
                                                                        alignItems: "center",
                                                                        height: "44px",
                                                                        marginTop: "8px",
                                                                        width: window.innerWidth < 768 ? 350 : 305
                                                                    },
                                                                },
                                                                error: methods.formState?.errors?.originalPurchaseDate?.message != undefined,
                                                            },
                                                            inputAdornment: {
                                                                sx: {
                                                                    height: "44px",
                                                                }
                                                            }
                                                        }}
                                                        localeText={{
                                                            fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                        }}
                                                    />
                                                </div>

                                            </div>
                                            {methods.formState?.errors?.originalPurchaseDate?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.originalPurchaseDate?.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="lastName">Mortgage Lender</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.mortgageHolder ? "" : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        methods.formState?.errors?.mortgageHolder?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id="mortgageHolder"
                                                    {...methods.register("mortgageHolder", {
                                                        required: "Enter the name of the mortgage lender",
                                                        maxLength: {
                                                            value: 200,
                                                            message: "Names exceeds maximum character count",
                                                        },
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9 '-.]*$/,
                                                            message: "Please enter the current mortgage lender",
                                                        },
                                                        onBlur: () => {
                                                            methods.setValue("mortgageHolder", methods.getValues("mortgageHolder").trim());
                                                        },
                                                    })}
                                                />
                                            </div>
                                            {methods.formState?.errors?.mortgageHolder?.message == undefined && <span className="flex items-center font-medium tracking-wide text-xs mt-1 ml-1">
                                                Name of the Financial Institution the mortgage is currently with.
                                            </span>}
                                            {methods.formState?.errors?.mortgageHolder?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.mortgageHolder.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="assetOwnership">Who owns this property?</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.propertyOwner?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <select
                                                    id="ownership"
                                                    className={clsx(
                                                        methods.formState?.errors?.propertyOwner?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                    {...methods.register("propertyOwner", {
                                                        required: "Select an option",
                                                    })}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    {applicantList.map((applicant) => (
                                                        <option key={applicant.applicantId} value={applicant.applicantId}>
                                                            {applicant.fullname}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.propertyOwner?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.propertyOwner?.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {application.applicationCategory == applicationCategory.Refinance &&
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="title">Total loan amount</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.totalLoan
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id="totalLoanAmount"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        value={totalLoanAmount}
                                                        maxLength={10}
                                                        className={clsx(
                                                            methods.formState?.errors?.totalLoan?.message
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        {...methods.register("totalLoan", {
                                                            required: "Enter the total loan amount",
                                                        })}
                                                        onValueChange={(value, name, values) => value != undefined ? setTotalLoanAmount(value) : setTotalLoanAmount("")}
                                                    />
                                                </div>
                                                {methods.formState?.errors?.totalLoan?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.totalLoan.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    }

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="propertyType">Property Type</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.propertyType
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <select
                                                    id="propertyType"
                                                    {...methods.register("propertyType", {
                                                        required: "Select an option",
                                                    })}
                                                    onChange={(e) => {
                                                        methods.clearErrors("maintenanceFee");
                                                    }}
                                                    className={clsx(
                                                        methods.formState?.errors?.propertyType
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="singlefamily">Single Family</option>
                                                    <option value="semidetached">Semi-Detached</option>
                                                    <option value="duplex">Duplex</option>
                                                    <option value="triplex">Triplex</option>
                                                    <option value="fourplex">Fourplex</option>
                                                    <option value="commercialmix">Commercial Mix</option>
                                                    <option value="condo">Condo</option>
                                                    <option value="townhouse">Townhouse</option>
                                                    <option value="vacationpropertytypea">Vacation Property-Type A</option>
                                                    <option value="vacationpropertytypeb">Vacation Property-Type B</option>
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.propertyType?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.propertyType?.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="phoneNumber">Estimated Livable Area</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.livableArea
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[110px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.livableAreaUnit
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        "mt-2 w:[220px] md:w-[150px]", style.livableArea
                                                    )}
                                                    {...methods.register("livableArea", {
                                                        required: "Enter the livable area of your home.",
                                                        validate: (livableArea) => {
                                                            if (+livableArea > 0) {
                                                                return true;
                                                            } else {
                                                                return "Please enter a valid number.";
                                                            }
                                                        }
                                                    })}
                                                    type="number"
                                                    placeholder=""
                                                    id="livableArea"
                                                    onInput={(e: any) => methods.setValue("livableArea", e.target.value.slice(0, 10))}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                                <select
                                                    id="livableAreaUnit"
                                                    {...methods.register("livableAreaUnit", {
                                                        required: "Select a unit",
                                                    })}
                                                    defaultValue=""
                                                    className={clsx(
                                                        style.frequency, "mt-2 pt-2.5 ml-3"
                                                    )}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="squarefeet">Sq. Ft</option>
                                                    <option value="squaremetres">Sq. Metres</option>
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.livableArea?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.livableArea.message}
                                                </span>
                                            )}
                                            {methods.formState?.errors?.livableAreaUnit?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.livableAreaUnit.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Estimated Annual property tax</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.propertyTax
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="propertyTax"
                                                    placeholder="/per year"
                                                    prefix={"$"}
                                                    suffix="/year"
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={propertyTaxAmount}
                                                    maxLength={7}
                                                    className={clsx(
                                                        methods.formState?.errors?.propertyTax?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("propertyTax", {
                                                        required: "Enter the property tax of your home.",
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setPropertyTaxAmount(value) : setPropertyTaxAmount("")}
                                                    transformRawValue={(value) => {
                                                        value = value.replace("/year", "");
                                                        if (value != undefined && value != "") {
                                                            methods.setValue("propertyTax", value + "/year");
                                                        }
                                                        return value
                                                    }}
                                                />
                                            </div>
                                            {methods.formState?.errors?.propertyTax?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.propertyTax.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Condo/Maintenance Fee (if applicable)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx("hidden",
                                                            "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="maintenanceFee"
                                                    placeholder="/per month"
                                                    prefix={"$"}
                                                    suffix="/month"
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={maintenanceAmount}
                                                    maxLength={7}
                                                    className={clsx(
                                                        methods.formState?.errors?.maintenanceFee?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("maintenanceFee", {
                                                        validate: {
                                                            condoFee: (c) => {
                                                                let currentAmount = c.replaceAll("$", "").replaceAll(",", "").replaceAll("/month", "");
                                                                if (methods.getValues("propertyType") != "condo" || +currentAmount > 0) {
                                                                    return true
                                                                } else if (c == undefined || c.trim() == "") {
                                                                    return "Enter the condo/maintenance fee."
                                                                } else {
                                                                    return "The condo/maintenance fee cannot be zero."
                                                                }
                                                            },
                                                        },
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setMaintenanceAmount(value) : setMaintenanceAmount("")}
                                                    transformRawValue={(value) => {
                                                        value = value.replace("/month", "");
                                                        if (value != undefined && value != "") {
                                                            methods.setValue("maintenanceFee", value + "/month");
                                                        }
                                                        return value
                                                    }}
                                                />
                                            </div>
                                            {methods.formState?.errors?.maintenanceFee?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.maintenanceFee.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Estimated Heating Cost</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx("hidden",
                                                            "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="heatingCost"
                                                    placeholder="/per month"
                                                    prefix={"$"}
                                                    suffix="/month"
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    maxLength={7}
                                                    value={heatingCostAmount}
                                                    className={clsx(
                                                        methods.formState?.errors?.heatingCost?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    {...methods.register("heatingCost", {
                                                        required: "Enter the estimated heating cost of your home.",
                                                        validate: {
                                                            heatingCost: (c) => {
                                                                let currentAmount = c.replaceAll("$", "").replaceAll(",", "").replaceAll("/month", "");
                                                                if (+currentAmount > 0) {
                                                                    return true
                                                                } else {
                                                                    return "The heating cost cannot be zero."
                                                                }
                                                            },
                                                        },
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setHeatingCostAmount(value) : setHeatingCostAmount("")}
                                                    transformRawValue={(value) => {
                                                        value = value.replace("/month", "");
                                                        if (value != undefined && value != "") {
                                                            methods.setValue("heatingCost", value + "/month");
                                                        }
                                                        return value
                                                    }}
                                                />
                                            </div>
                                            {methods.formState?.errors?.heatingCost?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.heatingCost?.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="title">Heating Type (Optional)</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx("hidden",
                                                            "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <select
                                                    id="title"
                                                    {...methods.register("heatingType", {})}
                                                    defaultValue=""
                                                    className={clsx(
                                                        methods.formState?.errors?.heatingType?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="electricbaseboard">Electric Baseboard</option>
                                                    <option value="forcedair">Forced Air Gas/Oil/Electric</option>
                                                    <option value="geothermal">Geothermal</option>
                                                    <option value="hotwater">Hot Water Heating</option>
                                                    <option value="oil">Oil</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="futureUsage">Future Usage</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.futureUsage?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <select
                                                    id="futureUsage"
                                                    {...methods.register("futureUsage", {
                                                        required: "Select an option.",
                                                    })}
                                                    onChange={(e) => {
                                                        setFutureUsageText(e.target.value);
                                                        methods.setValue("futureUsage", e.target.value);
                                                        methods.clearErrors("futureUsage");
                                                        if (e.target.value !== "selling") {
                                                            setPropertySold("");
                                                            methods.setValue("propertySold", "");
                                                            methods.clearErrors("propertySold");
                                                            methods.setValue("soldDate", "");
                                                            methods.clearErrors("soldDate");
                                                        }
                                                    }}
                                                    className={clsx(
                                                        methods.formState?.errors?.futureUsage
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="primary">Will remain as my Primary</option>
                                                    <option value="selling">Will be selling it</option>
                                                    <option value="renting">Will be renting it out</option>
                                                    <option value="vacationproperty">Will become secondary/vacation property</option>
                                                    <option value="endinglease">Will be ending my lease</option>
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.futureUsage?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.futureUsage?.message}
                                                </span>
                                            )}
                                        </div>
                                        {methods.getValues("futureUsage") === "selling" &&

                                            <div className={clsx("mt-5", style.twoColFirst)}>
                                                <label>Has the property been sold?</label>
                                                <div className="grid grid-flow-row sm:grid-flow-col">
                                                    <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                        <li className="relative">
                                                            <input
                                                                type="radio"
                                                                value="yes"
                                                                id={"yes-property-sold"}
                                                                {...methods.register("propertySold", {
                                                                    required: 'Select an option',
                                                                    onChange: propertySoldChangeHandler,
                                                                })}
                                                                checked={propertySold === "yes"}
                                                                onChange={propertySoldChangeHandler}
                                                                className="sr-only peer "
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.phoneLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                    methods.formState?.errors?.propertySold?.message
                                                                        ? style.labelError
                                                                        : ""
                                                                )}
                                                                htmlFor={"yes-property-sold"}
                                                            >
                                                                <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                            </label>
                                                        </li>
                                                        <li className="relative">
                                                            <input
                                                                type="radio"
                                                                value="no"
                                                                id={"no-property-sold"}
                                                                {...methods.register("propertySold", {
                                                                    required: 'Select an option',
                                                                    onChange: propertySoldChangeHandler,
                                                                })}
                                                                checked={propertySold === "no"}
                                                                onChange={propertySoldChangeHandler}
                                                                className="sr-only peer leading-6"
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.phoneLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                    methods.formState?.errors?.propertySold?.message
                                                                        ? style.labelError
                                                                        : ""
                                                                )}
                                                                htmlFor={"no-property-sold"}
                                                            >
                                                                <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                            </label>
                                                        </li>
                                                    </ul>
                                                </div>
                                                {methods.formState?.errors?.propertySold?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState?.errors?.propertySold?.message}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                    </div>

                                    {propertySold === "yes" &&
                                        <div className="flex md:flex-row flex-col">
                                            <div className="mt-5">
                                                <label htmlFor="soldDate">Date of Sale</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.soldDate?.message
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <div>
                                                        <DatePicker
                                                            className={style.datePickerInput}
                                                            format="DD MMM YYYY"
                                                            minDate={dayjs()}
                                                            value={dayjs(soldDateText, 'DD/MM/YYYY')}
                                                            onChange={(e) => {
                                                                if (e != undefined) {
                                                                    methods.setValue("soldDate", e.format('DD/MM/YYYY'));
                                                                    setSoldDateText(e.format('DD/MM/YYYY'));
                                                                    setDisplayError(false);
                                                                    methods.clearErrors("soldDate");
                                                                }
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    InputProps: {
                                                                        sx: {
                                                                            borderRadius: '12px',
                                                                            backgroundColor: 'white',
                                                                            padding: "0px",
                                                                            alignItems: "center",
                                                                            height: "44px",
                                                                            marginTop: "8px",
                                                                            width: window.innerWidth < 768 ? 350 : 305
                                                                        },
                                                                    },
                                                                    error: methods.formState?.errors?.soldDate?.message != undefined,
                                                                },
                                                                inputAdornment: {
                                                                    sx: {
                                                                        height: "44px",
                                                                    }
                                                                }
                                                            }}
                                                            localeText={{
                                                                fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                            }}
                                                        />
                                                    </div>

                                                </div>
                                                {methods.formState?.errors?.soldDate?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState?.errors?.soldDate?.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    }

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}